(function () {
  const STORAGE_KEY = 'wishlist-items';
  const LEGACY_STORAGE_KEY = 'wishlist';
  const SHARE_EVENT_NAME = 'Wishlist Shared';
  const UPDATE_EVENTS = ['wishlist:item-added', 'wishlist:item-removed', 'wishlist:cleared'];
  let wishlistApiClient = null;
  let gadgetBootstrapStarted = false;
  let cachedRemoteIds = null;

  function getGadgetGraphqlEndpoint() {
    const script = document.querySelector('script[src*="gadget.app/api/client/"]');
    if (!script) return null;

    try {
      const url = new URL(script.src);
      return `${url.origin}/api/graphql`;
    } catch (error) {
      return null;
    }
  }

  async function callManageWishlistViaGraphQL(payload) {
    const endpoint = getGadgetGraphqlEndpoint();
    if (!endpoint) {
      throw new Error('Could not resolve Gadget GraphQL endpoint');
    }

    const query = `
      mutation ManageWishlist($action: String!, $customerId: String!, $productId: String, $shopId: String) {
        manageWishlist(action: $action, customerId: $customerId, productId: $productId, shopId: $shopId) {
          success
          message
          wishlist
        }
      }
    `;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query,
        variables: payload
      })
    });

    const json = await response.json();
    if (!response.ok || json.errors) {
      throw new Error(`GraphQL manageWishlist failed: ${JSON.stringify(json.errors || json)}`);
    }

    return json.data?.manageWishlist;
  }

  async function callManageWishlist(payload) {
    const fullPayload = { shopId: String(theme?.wishlist?.shopId || ''), ...payload };
    const baseClient = getWishlistApiClient();
    const usingNativeManageWishlist = Boolean(baseClient && typeof baseClient.manageWishlist === 'function');

    const tryCall = async (nextPayload) => {
      const client = getWishlistApiClient();
      if (!client) {
        throw new Error('Wishlist API client unavailable');
      }

      if (typeof client.manageWishlist === 'function') {
        return client.manageWishlist(nextPayload);
      }

      if (typeof client.mutate === 'function') {
        // Fallback for generated Gadget clients that expose only generic mutate/query methods.
        try {
          return await client.mutate({
            operationName: 'manageWishlist',
            variables: nextPayload
          });
        } catch (error) {
          // Some Gadget client builds expect a different mutate signature.
          return callManageWishlistViaGraphQL(nextPayload);
        }
      }

      return callManageWishlistViaGraphQL(nextPayload);
    };

    const isNumericCustomerId = /^\d+$/.test(String(fullPayload.customerId || ''));
    const gidCustomerId = isNumericCustomerId ? `gid://shopify/Customer/${fullPayload.customerId}` : String(fullPayload.customerId || '');

    try {
      return await tryCall(fullPayload);
    } catch (error) {
      const message = String(error?.message || error || '');
      const shouldRetryWithGid = !usingNativeManageWishlist && isNumericCustomerId && /Owner does not exist/i.test(message);
      if (!shouldRetryWithGid) throw error;

      return tryCall({
        ...fullPayload,
        customerId: gidCustomerId
      });
    }
  }

  function parseJSON(value, fallback) {
    try {
      const parsed = JSON.parse(value);
      return parsed == null ? fallback : parsed;
    } catch (error) {
      return fallback;
    }
  }

  function extractNumericId(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const match = value.match(/(\d+)$/);
      if (match) return Number(match[1]);
    }
    if (value && typeof value === 'object' && 'id' in value) {
      return extractNumericId(value.id);
    }
    return NaN;
  }

  function setCachedRemoteIdsFromArray(ids) {
    if (!Array.isArray(ids)) return;
    const normalized = Array.from(new Set(ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))));
    cachedRemoteIds = normalized;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  }

  const WishlistState = {
    get enabled() {
      return Boolean(theme?.config?.hasLocalStorage) && Boolean(theme?.wishlist?.enabled);
    },

    get requireLogin() {
      return Boolean(theme?.wishlist?.wishlistRequireLogin);
    },

    get customerLoggedIn() {
      return Boolean(theme?.wishlist?.customerLoggedIn);
    },

    get accessible() {
      return !this.requireLogin || this.customerLoggedIn;
    },

    get customerId() {
      return String(theme?.wishlist?.customerId || '');
    },

    get hasGadgetApi() {
      const client = getWishlistApiClient();
      return Boolean(client && (typeof client.manageWishlist === 'function' || typeof client.mutate === 'function'));
    },

    initStorage() {
      if (!this.enabled) return;

      const existing = parseJSON(window.localStorage.getItem(STORAGE_KEY) || '[]', []);
      if (Array.isArray(existing) && existing.length > 0) return;

      const legacy = parseJSON(window.localStorage.getItem(LEGACY_STORAGE_KEY) || '[]', []);
      if (Array.isArray(legacy) && legacy.length > 0) {
        const migrated = legacy
          .map((item) => extractNumericId(item && typeof item === 'object' ? item.id : item))
          .filter((id) => Number.isFinite(id));
        if (migrated.length > 0) {
          this.setAllItems(migrated);
          return;
        }
      }

      if (this.accessible) {
        const rawRemote = Array.isArray(theme?.wishlist?.customerWishlistItems) ? theme.wishlist.customerWishlistItems : [];
        const remoteIds = rawRemote
          .map((item) => extractNumericId(item))
          .filter((id) => Number.isFinite(id));
        if (remoteIds.length > 0) {
          this.setAllItems(remoteIds);
        }
        setCachedRemoteIdsFromArray(remoteIds);
      }
    },

    getAllItems() {
      if (!this.enabled) return [];

      const list = parseJSON(window.localStorage.getItem(STORAGE_KEY) || '[]', []);
      if (!Array.isArray(list)) return [];

      return list.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    },

    getItems() {
      if (!this.accessible) return [];
      if (this.accessible && cachedRemoteIds !== null) {
        return [...cachedRemoteIds];
      }
      return this.getAllItems();
    },

    setAllItems(items) {
      if (!this.enabled) return;
      const normalized = Array.from(new Set(items.map((id) => Number(id)).filter((id) => Number.isFinite(id))));
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    },

    hasItem(productId) {
      if (!this.accessible) return false;
      return this.getItems().includes(Number(productId));
    },

    async addItem(productId) {
      if (!this.enabled || !this.accessible) return false;

      const id = Number(productId);
      if (!Number.isFinite(id)) return false;

      const items = this.getItems();
      if (items.includes(id)) return false;

      if (this.hasGadgetApi && this.customerId) {
        try {
          const result = await callManageWishlist({
            action: 'add',
            customerId: this.customerId,
            productId: id.toString()
          });
          if (result && Array.isArray(result.wishlist)) {
            const ids = result.wishlist.map((item) => extractNumericId(item)).filter((n) => Number.isFinite(n));
            setCachedRemoteIdsFromArray(ids);
          } else {
            items.push(id);
            this.setAllItems(items);
          }
        } catch (error) {
          console.error('Error syncing wishlist add:', error);
          items.push(id);
          this.setAllItems(items);
        }
      } else {
        items.push(id);
        this.setAllItems(items);
      }

      trackKlaviyo('Wishlist Item Added', { productId: id, count: this.getItems().length });
      return true;
    },

    async removeItem(productId) {
      if (!this.enabled || !this.accessible) return false;

      const id = Number(productId);
      if (!Number.isFinite(id)) return false;

      const items = this.getItems();
      const next = items.filter((itemId) => itemId !== id);
      if (next.length === items.length) return false;

      if (this.hasGadgetApi && this.customerId) {
        try {
          const result = await callManageWishlist({
            action: 'remove',
            customerId: this.customerId,
            productId: id.toString()
          });
          if (result && Array.isArray(result.wishlist)) {
            const ids = result.wishlist.map((item) => extractNumericId(item)).filter((n) => Number.isFinite(n));
            setCachedRemoteIdsFromArray(ids);
          } else {
            this.setAllItems(next);
          }
        } catch (error) {
          console.error('Error syncing wishlist remove:', error);
          this.setAllItems(next);
        }
      } else {
        this.setAllItems(next);
      }

      trackKlaviyo('Wishlist Item Removed', { productId: id, count: this.getItems().length });
      return true;
    },

    async clearItems() {
      if (!this.enabled || !this.accessible) return false;

      if (this.hasGadgetApi && this.customerId) {
        try {
          const result = await callManageWishlist({
            action: 'reset',
            customerId: this.customerId
          });
          if (result && Array.isArray(result.wishlist)) {
            setCachedRemoteIdsFromArray(result.wishlist.map((item) => extractNumericId(item)).filter((id) => Number.isFinite(id)));
          } else {
            setCachedRemoteIdsFromArray([]);
          }
        } catch (error) {
          console.error('Error syncing wishlist reset:', error);
          setCachedRemoteIdsFromArray([]);
        }
      } else {
        this.setAllItems([]);
      }

      trackKlaviyo('Wishlist Cleared', { count: 0 });
      return true;
    },

    async initializeFromShopify() {
      if (!this.hasGadgetApi || !this.customerId) return;

      const rawRemote = Array.isArray(theme?.wishlist?.customerWishlistItems) ? theme.wishlist.customerWishlistItems : [];
      const remoteIds = rawRemote
        .map((item) => extractNumericId(item))
        .filter((id) => Number.isFinite(id));

      const localIds = this.getAllItems();
      const localSet = new Set(localIds);
      const remoteSet = new Set(remoteIds);

      const toRemote = localIds.filter((id) => !remoteSet.has(id));
      const toLocal = remoteIds.filter((id) => !localSet.has(id));

      for (const id of toRemote) {
        try {
          await callManageWishlist({
            action: 'add',
            customerId: this.customerId,
            productId: String(id)
          });
        } catch (error) {
          console.error(`Failed to sync local wishlist item ${id}:`, error);
        }
      }

      const mergedIds = remoteIds.concat(toRemote);
      setCachedRemoteIdsFromArray(mergedIds);
    }
  };

  function createHeartMarkup(active) {
    if (active) {
      return '<svg class="icon icon--heart" width="13" height="12" viewBox="0 0 13 12" stroke="currentColor" fill="none"><path style="fill:#FF6647;" d="M6.5 1.03c-0.71-0.67-1.64-1.04-2.6-1.04-0.51 0-1.02 0.1-1.49 0.31-0.47 0.2-0.9 0.5-1.25 0.87C-0.38 2.75-0.38 5.21 1.15 6.78l4.78 4.89c0.11 0.2 0.32 0.34 0.57 0.34 0.1 0 0.2-0.03 0.29-0.08 0.09-0.05 0.17-0.12 0.23-0.2l4.83-4.94c1.53-1.57 1.53-4.03-0.01-5.61C11.49 0.8 11.06 0.5 10.59 0.3 10.12 0.1 9.61 0 9.1 0c-0.96 0-1.89 0.37-2.6 1.03z"></path></svg>';
    }
    return '<svg class="icon icon--heart" width="13" height="12" viewBox="0 0 13 12" stroke="currentColor" fill="none"><path d="M6.5 1.03C5.79 0.37 4.86 0 3.9 0c-0.51 0-1.02 0.1-1.49 0.31-0.47 0.2-0.9 0.5-1.25 0.87C-0.38 2.75-0.38 5.21 1.15 6.78l4.78 4.89c0.11 0.2 0.32 0.34 0.57 0.34 0.1 0 0.2-0.03 0.29-0.08 0.09-0.05 0.17-0.12 0.23-0.2l4.83-4.94c1.53-1.57 1.53-4.03-0.01-5.61C11.49 0.8 11.06 0.5 10.59 0.3 10.12 0.1 9.61 0 9.1 0c-0.96 0-1.89 0.37-2.6 1.03z"></path></svg>';
  }

  function setButtonState(button, active) {
    if (!button) return;

    button.classList.toggle('is-active', active);
    button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');

    const text = button.querySelector('.btn-text');
    if (text) text.innerHTML = createHeartMarkup(active);
  }

  function updateWishlistButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      const productId = button.getAttribute('data-product-id');
      setButtonState(button, WishlistState.hasItem(productId));
    });
  }

  function updateWishlistCount() {
    const count = WishlistState.getItems().length;
    document.querySelectorAll('wishlist-count').forEach((counter) => {
      counter.textContent = String(count);
      counter.setAttribute('aria-label', `${count} ${count === 1 ? 'item' : 'items'} in wishlist`);
    });
  }

  function updateWishlistUI() {
    updateWishlistButtons();
    updateWishlistCount();
  }

  function trackKlaviyo(eventName, payload) {
    if (Array.isArray(window._learnq)) {
      window._learnq.push(['track', eventName, payload || {}]);
    }
  }

  function buildWishlistShareUrl() {
    const items = WishlistState.getItems();
    if (items.length === 0) return window.location.origin + (theme?.routes?.search_url || '/search');
    const q = items.map((id) => `id:${id}`).join(' OR ');
    return `${window.location.origin}${theme?.routes?.search_url || '/search'}?type=product&q=${encodeURIComponent(q)}`;
  }

  function handleWishlistShare(channel) {
    const shareUrl = buildWishlistShareUrl();
    const shareText = 'My wishlist';
    const count = WishlistState.getItems().length;

    if (count === 0) return;

    if (channel === 'copy') {
      navigator.clipboard?.writeText(shareUrl).catch(() => {});
    } else if (channel === 'email') {
      window.open(
        `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(shareUrl)}`,
        '_blank'
      );
    } else if (channel === 'facebook') {
      window.open(`https://www.facebook.com/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener');
    } else if (channel === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener');
    } else if (channel === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}: ${shareUrl}`)}`, '_blank', 'noopener');
    }

    trackKlaviyo(SHARE_EVENT_NAME, { channel, count, shareUrl });
  }

  function openWishlistDrawer() {
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {
        tab: 'wishlist',
        open: true
      }
    }));
  }

  async function onWishlistButtonClick(button) {
    if (!WishlistState.accessible) {
      window.location.href = theme?.routes?.account_login_url || '/account/login';
      return;
    }

    const productId = Number(button.getAttribute('data-product-id'));
    const closeQuickview = button.getAttribute('data-close-quickview');
    const isInsideProductCard = button.closest('.product-card') !== null || button.closest('.product-form__wishlist') !== null;
    const isInWishlistDrawer = button.closest('wishlist-items') !== null || button.closest('#WishlistItems') !== null;
    const inWishlist = WishlistState.hasItem(productId);

    const success = inWishlist
      ? await WishlistState.removeItem(productId)
      : await WishlistState.addItem(productId);

    if (!success) return;

    updateWishlistUI();

    const eventName = inWishlist ? 'wishlist:item-removed' : 'wishlist:item-added';
    const detail = inWishlist
      ? { productRemoved: productId, count: WishlistState.getItems().length }
      : { productAdded: productId, count: WishlistState.getItems().length };

    document.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      detail
    }));

    if (closeQuickview) {
      button.closest('.quick-view')?.querySelector('.button--close')?.click();
    }

    if (isInsideProductCard && !isInWishlistDrawer) {
      openWishlistDrawer();
    }
  }

  class WishlistItems extends HTMLElement {
    constructor() {
      super();
      this.limit = this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit'), 10) : 10;
      this.context = this.getAttribute('data-context');
      this.sectionId = this.getAttribute('data-section-id');
      this._boundRefresh = this.reinitialize.bind(this);
      this._boundRerender = this.renderSection.bind(this);
    }

    connectedCallback() {
      if (!WishlistState.enabled) {
        this.hidden = true;
        return;
      }

      UPDATE_EVENTS.forEach((eventType) => {
        document.addEventListener(eventType, this.context === 'section' && this.sectionId ? this._boundRerender : this._boundRefresh);
      });
      this.init();
    }

    disconnectedCallback() {
      UPDATE_EVENTS.forEach((eventType) => {
        document.removeEventListener(eventType, this.context === 'section' && this.sectionId ? this._boundRerender : this._boundRefresh);
      });
    }

    reinitialize() {
      this.innerHTML = '';
      this.init();
    }

    init() {
      if (!WishlistState.accessible) {
        this.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <h2 class="drawer__empty-text heading leading-none tracking-tight">Please log in to view your wishlist</h2>
            <a href="${theme?.routes?.account_login_url || '/account/login'}" class="button button--primary button--md" is="hover-link">
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">Login</span>
            </a>
          </div>
        </div>
        `;
        return;
      }

      const items = WishlistState.getItems().slice().reverse();
      if (items.length === 0) {
        this.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <h2 class="drawer__empty-text heading leading-none tracking-tight">Your wishlist is empty</h2>
          </div>
        </div>
        `;
        return;
      }

      if (this.context !== 'section') {
        this.fetchProductBatch(items);
      }
    }

    fetchProductBatch(items, startIndex = 0) {
      const endIndex = Math.min(startIndex + this.limit, items.length);
      const batch = items.slice(startIndex, endIndex);
      if (batch.length === 0) return;

      const query = batch.map((item) => `id:${item}`).join(' OR ');
      fetch((this.getAttribute('data-url') || '') + query)
        .then((response) => response.text())
        .then((responseText) => {
          const sectionRoot = new DOMParser().parseFromString(responseText, 'text/html').querySelector('.shopify-section');
          if (!sectionRoot) return;

          const recommendations = sectionRoot.querySelector('wishlist-items');
          if (recommendations && recommendations.innerHTML.trim().length) {
            if (startIndex === 0) {
              this.innerHTML = recommendations.innerHTML;
            } else {
              const container = this.querySelector('.horizontal-products');
              recommendations.querySelectorAll('.horizontal-product').forEach((card) => {
                container?.appendChild(card.cloneNode(true));
              });
            }

            updateWishlistButtons();

            if (endIndex < items.length) {
              this.fetchProductBatch(items, endIndex);
            }
          } else if (startIndex === 0) {
            this.innerHTML = `
            <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
              <div class="drawer__empty grid gap-5 md:gap-8">
                <h2 class="drawer__empty-text heading leading-none tracking-tight">Your wishlist is empty</h2>
              </div>
            </div>
            `;
          }
        })
        .catch((error) => {
          console.error('Error fetching wishlist batch:', error);
          if (endIndex < items.length) this.fetchProductBatch(items, endIndex);
        });
    }

    renderSection() {
      if (!this.sectionId) return;

      fetch(`${window.location.pathname}?section_id=${this.sectionId}`)
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newSection = doc.querySelector(`[data-section-id="${this.sectionId}"]`);
          if (newSection && this.parentNode) {
            this.parentNode.replaceChild(newSection, this);
          }
        })
        .catch((error) => {
          console.error('Error re-rendering wishlist section:', error);
        });
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this._boundUpdate = () => updateWishlistCount();
      UPDATE_EVENTS.forEach((eventType) => document.addEventListener(eventType, this._boundUpdate));
      updateWishlistCount();
    }

    disconnectedCallback() {
      UPDATE_EVENTS.forEach((eventType) => document.removeEventListener(eventType, this._boundUpdate));
    }
  }

  function bindDocumentEvents() {
    if (window.__wishlistHandlersBound) return;
    window.__wishlistHandlersBound = true;

    document.addEventListener('cart:refresh', (event) => {
      if (event.detail?.tab === 'wishlist') {
        syncWishlistFromGadgetOnDrawerOpen();
      }
    });

    document.addEventListener('click', async (event) => {
      const toggleButton = event.target.closest('[data-wishlist-toggle-button]');
      if (toggleButton) {
        event.preventDefault();
        openWishlistDrawer();
        return;
      }

      const shareButton = event.target.closest('[data-wishlist-share]');
      if (shareButton) {
        event.preventDefault();
        handleWishlistShare(shareButton.getAttribute('data-wishlist-share'));
        return;
      }

      const clearButton = event.target.closest('[data-wishlist-clear]');
      if (clearButton) {
        event.preventDefault();
        if (await WishlistState.clearItems()) {
          updateWishlistUI();
          document.dispatchEvent(new CustomEvent('wishlist:cleared', {
            bubbles: true,
            detail: { count: 0 }
          }));
        }
        return;
      }

      const button = event.target.closest('[data-wishlist-button]');
      if (!button) return;

      event.preventDefault();
      await onWishlistButtonClick(button);
    });

    document.addEventListener('slider-element:loaded', updateWishlistButtons);
    document.addEventListener('product-info:loaded', updateWishlistButtons);
    document.addEventListener('collection:reloaded', updateWishlistButtons);
    document.addEventListener('customer:login', async () => {
      if (!WishlistState.hasGadgetApi) return;
      await WishlistState.initializeFromShopify();
      updateWishlistUI();
      document.querySelectorAll('wishlist-items').forEach((items) => items.reinitialize());
    });
  }

  function getWishlistApiClient() {
    if (wishlistApiClient && typeof wishlistApiClient.manageWishlist === 'function') {
      return wishlistApiClient;
    }

    if (window.api && typeof window.api.manageWishlist === 'function') {
      wishlistApiClient = window.api;
      return wishlistApiClient;
    }

    if (typeof window.MagnaTilesWishlistClient !== 'undefined') {
      try {
        const client = new window.MagnaTilesWishlistClient();
        if (client && (typeof client.manageWishlist === 'function' || typeof client.mutate === 'function')) {
          window.api = client;
          wishlistApiClient = client;
          return wishlistApiClient;
        }
      } catch (error) {
        console.error('Wishlist API lazy init failed:', error);
      }
    }

    return null;
  }

  function initializeGadgetApi() {
    if (getWishlistApiClient()) return;

    try {
      if (typeof window.MagnaTilesWishlistClient !== 'undefined') {
        const client = new window.MagnaTilesWishlistClient();
        if (client && (typeof client.manageWishlist === 'function' || typeof client.mutate === 'function')) {
          window.api = client;
          wishlistApiClient = client;
          return;
        }
      }

      if (typeof window.Gadget !== 'undefined') {
        const client = new window.Gadget();
        if (client && (typeof client.manageWishlist === 'function' || typeof client.mutate === 'function')) {
          window.api = client;
          wishlistApiClient = client;
        }
      }
    } catch (error) {
      console.error('Wishlist API client init failed:', error);
    }
  }

  let shopifyHydrateDone = false;

  async function tryHydrateFromShopify() {
    if (!WishlistState.enabled || !WishlistState.customerLoggedIn || !WishlistState.hasGadgetApi || shopifyHydrateDone) return;
    shopifyHydrateDone = true;
    try {
      await WishlistState.initializeFromShopify();
      updateWishlistUI();
      document.querySelectorAll('wishlist-items').forEach((el) => el.reinitialize());
    } catch (error) {
      shopifyHydrateDone = false;
      console.error('Wishlist hydrate from Shopify failed:', error);
    }
  }

  async function syncWishlistFromGadgetOnDrawerOpen() {
    if (!WishlistState.enabled || !WishlistState.customerLoggedIn || !WishlistState.hasGadgetApi) return;
    try {
      const result = await callManageWishlist({
        action: 'get',
        customerId: WishlistState.customerId
      });
      if (result && Array.isArray(result.wishlist)) {
        const ids = result.wishlist.map((item) => extractNumericId(item)).filter((n) => Number.isFinite(n));
        setCachedRemoteIdsFromArray(ids);
        updateWishlistUI();
        document.querySelectorAll('wishlist-items').forEach((el) => el.reinitialize());
      }
    } catch (error) {
      console.error('Wishlist sync from Gadget on drawer open failed:', error);
    }
  }

  function startGadgetBootstrap() {
    if (gadgetBootstrapStarted) return;
    gadgetBootstrapStarted = true;

    initializeGadgetApi();
    if (getWishlistApiClient()) {
      tryHydrateFromShopify();
      return;
    }

    let attempts = 0;
    const maxAttempts = 30;
    const timer = window.setInterval(() => {
      attempts += 1;
      initializeGadgetApi();
      if (getWishlistApiClient() || attempts >= maxAttempts) {
        window.clearInterval(timer);
        tryHydrateFromShopify();
      }
    }, 250);

    document.querySelectorAll('script[src*="gadget.app"]').forEach((script) => {
      script.addEventListener('load', () => {
        initializeGadgetApi();
        tryHydrateFromShopify();
      }, { once: true });
    });

    window.addEventListener('load', () => {
      initializeGadgetApi();
      tryHydrateFromShopify();
    }, { once: true });
  }

  async function initializeWishlist() {
    if (!WishlistState.enabled) return;

    WishlistState.initStorage();
    startGadgetBootstrap();

    if (WishlistState.customerLoggedIn && WishlistState.hasGadgetApi) {
      await tryHydrateFromShopify();
    }

    updateWishlistUI();
  }

  if (!customElements.get('wishlist-items')) customElements.define('wishlist-items', WishlistItems);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);

  bindDocumentEvents();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWishlist, { once: true });
  } else {
    initializeWishlist();
  }
})();